import type { Shift } from "@/features/shifts/types";

/** Sentinel id for a shift that exists only until start-shift sync completes. */
export const OFFLINE_SHIFT_PLACEHOLDER_ID = -9000000001;

export function isOfflineProvisionalShift(shift: Shift | null | undefined): boolean {
  return shift?.offlinePending === true || shift?.id === OFFLINE_SHIFT_PLACEHOLDER_ID;
}

/** Omit when posting patrol so the server does not receive a fake shift id. */
export function patrolShiftIdForApi(shift: Shift | null | undefined): number | undefined {
  if (shift == null || isOfflineProvisionalShift(shift)) return undefined;
  return shift.id;
}

export function buildProvisionalStartShift(
  vars: {
    notes?: string;
    manualArrivalTime?: string;
    coords?: { latitude: number; longitude: number } | null;
  },
  employeeId: number,
  startedAtIso: string
): Shift {
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone ?? null;
  return {
    id: OFFLINE_SHIFT_PLACEHOLDER_ID,
    employee_id: employeeId,
    started_at: startedAtIso,
    ended_at: null,
    notes: vars.notes ?? null,
    created_at: startedAtIso,
    updated_at: startedAtIso,
    manual_arrival_time: vars.manualArrivalTime ?? null,
    timezone: tz,
    start_latitude: vars.coords?.latitude ?? null,
    start_longitude: vars.coords?.longitude ?? null,
    offlinePending: true,
  };
}

export function buildProvisionalEndedShift(
  open: Shift | null | undefined,
  vars: {
    notes?: string;
    manualEndTime?: string;
    endOfShiftChecklist?: Array<{ item: string; completed: boolean }>;
    coords?: { latitude: number; longitude: number } | null;
  }
): Shift {
  const now = new Date().toISOString();
  const baseId = open?.id ?? OFFLINE_SHIFT_PLACEHOLDER_ID;
  const baseEmployee = open?.employee_id ?? 0;
  const started = open?.started_at ?? now;
  return {
    id: baseId,
    employee_id: baseEmployee,
    started_at: started,
    ended_at: now,
    notes: vars.notes ?? open?.notes ?? null,
    created_at: open?.created_at ?? started,
    updated_at: now,
    manual_arrival_time: open?.manual_arrival_time ?? null,
    manual_end_time: vars.manualEndTime ?? null,
    end_of_shift_checklist: vars.endOfShiftChecklist,
    timezone: open?.timezone ?? null,
    start_latitude: open?.start_latitude ?? null,
    start_longitude: open?.start_longitude ?? null,
    end_latitude: vars.coords?.latitude ?? null,
    end_longitude: vars.coords?.longitude ?? null,
    offlinePendingEnd: true,
  };
}
